import { ActionContext, ActionTree } from 'vuex';
import { State } from '@/store';
import { Pricing, PricingMode } from '@/app/domain/Pricing';
import { Setting } from '@/app/domain/Setting';
import { Company } from '@/app/domain/Company';
import { Contact, isContactAdded } from '@/app/domain/Contact';
import { isCompanyWizardStepFilled, isContactsWizardStepFilled } from '@/app/domain/Wizard';
import { EmailNotificationsSettings } from '@/app/domain/EmailNotificationsSettings';
import { PricingService } from '@/app/services/PricingService';
import { SettingService } from '@/app/services/SettingService';
import { SelfServiceModeService } from '@/app/services/SelfServiceModeService';
import { CompanyService } from '@/app/services/CompanyService';
import {
  ContactService,
  CreateContactPayload,
  EditContactPayload,
  DeleteContactPayload,
  SendVerificationEmailPayload
} from '@/app/services/ContactService';
import { ConsumerProfileService, ConsumerProfileInformation, ResetPasswordPayload } from '@/app/services/ConsumerProfileService';
import { DeleteAccountConfirmationPayload, DeleteAccountReason, DeleteAccountService, TenantLicensesResponse } from '@/app/services/DeleteAccountService';
import { filterOutUndefinedObjectFields } from '@/app/library/filterOutUndefinedObjectFields';
import { ModuleState } from './state';

export function createActions(
  pricingService: PricingService,
  settingService: SettingService,
  selfServiceModeService: SelfServiceModeService,
  companyService: CompanyService,
  contactService: ContactService,
  consumerProfileService: ConsumerProfileService,
  deleteAccountService: DeleteAccountService
): ActionTree<ModuleState, State> {
  return {
    async fetchWizard(context: ActionContext<ModuleState, State>): Promise<void> {
      context.commit('setIsWizardFetching', true);
      await Promise.all([
        context.dispatch('fetchPricing'),
        context.dispatch('fetchSetting'),
        context.dispatch('fetchProfile')
      ]);
      context.commit('setIsWizardFetching', false);

      const isWizardAvailable = context.state.pricing?.mode === PricingMode.Production
        && context.state.setting?.enabled === true
        && (!isCompanyWizardStepFilled(context.state.company || {})
          || !isContactsWizardStepFilled(context.state.addedContactList));
      context.commit('setIsWizardAvailable', isWizardAvailable);
    },
    async saveCompanyWizardStep(context: ActionContext<ModuleState, State>, company: Company): Promise<void> {
      await companyService.editCompany({
        ...context.state.company,
        ...filterOutUndefinedObjectFields(company)
      });
    },
    async saveContactsWizardStep(context: ActionContext<ModuleState, State>, contacts: Contact[]): Promise<void> {
      const existingContacts: Contact[] = contacts.filter((contact: Contact): boolean => !!contact.id);
      const newContacts: Contact[] = contacts.filter((contact: Contact): boolean => !contact.id);

      await Promise.all([
        ...existingContacts.map((contact) => contactService.editContact(contact)),
        ...newContacts.map((contact) => contactService.createContact(contact))
      ]);
    },
    async fetchPricing(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsPricingFetching', true);
        const pricing: Pricing | null = await pricingService.getPricing();
        context.commit('setPricing', pricing);
        context.commit('setIsPricingFetching', false);
      } catch (error) {
        context.commit('setIsPricingFetching', false);
      }
    },
    async fetchSetting(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsSettingFetching', true);
        const setting: Setting | null = await settingService.getSetting();
        context.commit('setSetting', setting);
        context.commit('setIsSettingFetching', false);
      } catch (error) {
        context.commit('setIsSettingFetching', false);
      }
    },
    async fetchProfile(context: ActionContext<ModuleState, State>): Promise<void> {
      context.commit('setIsProfileFetching', true);
      await Promise.all([
        context.dispatch('fetchSelfServiceMode'),
        context.dispatch('fetchCustomerType'),
        context.dispatch('fetchCompany'),
        context.dispatch('fetchContactList'),
        context.dispatch('fetchTenantContactList'),
        context.dispatch('fetchIsDirectPartner')
      ]);
      context.commit('setIsProfileFetching', false);
    },
    async fetchSelfServiceMode(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsSelfServiceModeFetching', true);
        const selfServiceMode: boolean = await selfServiceModeService.getSelfServiceMode();
        context.commit('setSelfServiceMode', selfServiceMode);
        context.commit('setIsSelfServiceModeFetching', false);
      } catch (error) {
        context.commit('setIsSelfServiceModeFetching', false);
      }
    },
    async fetchCompany(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsCompanyFetching', true);
        const company: Company = await companyService.getCompany();
        context.commit('setCompany', company);
        context.commit('setIsCompanyFetching', false);
      } catch (error) {
        context.commit('setIsCompanyFetching', false);
      }
    },
    async editCompanyAndContacts(
      context: ActionContext<ModuleState, State>,
      company: Company
    ): Promise<void> {
      try {
        context.commit('setIsCompanyEditing', true);
        await Promise.all([
          companyService.editCompanyInfo(company),
          contactService.editCompanyContact(company.legalContact!),
          contactService.editCompanyContact(company.billingContact!)
        ]);

        context.dispatch('fetchCompany');
        context.dispatch('fetchTenantContactList');

        context.commit('setIsCompanyEditing', false);
      } catch (error) {
        context.commit('setIsCompanyEditing', false);
      }
    },
    async fetchContactList(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsContactListFetching', true);
        const contactList: Contact[] = await contactService.getContactList();
        const addedContactList: Contact[] = contactList
          .filter((contact: Contact) => isContactAdded(contact));
        const notAddedContactList: Contact[] = contactList
          .filter((contact: Contact) => !isContactAdded(contact));
        context.commit('setContactList', contactList);
        context.commit('setAddedContactList', addedContactList);
        context.commit('setNotAddedContactList', notAddedContactList);
        context.commit('setIsContactListFetching', false);
      } catch (error) {
        context.commit('setIsContactListFetching', false);
      }
    },
    async fetchTenantContactList(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsTenantContactListFetching', true);
        const contactList: Contact[] = await contactService.getFullTenantContactList();
        context.commit('setTenantContactList', contactList);
        context.commit('setIsTenantContactListFetching', false);
      } catch (error) {
        context.commit('setIsTenantContactListFetching', false);
      }
    },
    async fetchIsDirectPartner(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsDirectPartnerFetching', true);
        const isDirectPartner: boolean = await companyService.isDirectPartner();
        context.commit('setIsDirectPartner', isDirectPartner);
        context.commit('setIsDirectPartnerFetching', false);
      } catch (error) {
        context.commit('setIsDirectPartnerFetching', false);
      }
    },
    async createContact(context: ActionContext<ModuleState, State>, payload: CreateContactPayload): Promise<void> {
      try {
        context.commit('setIsContactCreating', true);
        await contactService.createContact(payload);
        context.commit('setIsContactCreating', false);
        context.dispatch('fetchContactList');
        context.dispatch('fetchTenantContactList');
      } catch (error) {
        context.commit('setIsContactCreating', false);
      }
    },
    async editContact(context: ActionContext<ModuleState, State>, payload: EditContactPayload): Promise<void> {
      try {
        context.commit('setIsContactEditing', true);
        await contactService.editContact(payload);
        context.commit('setIsContactEditing', false);
        context.dispatch('fetchContactList');
        context.dispatch('fetchTenantContactList');
      } catch (error) {
        context.commit('setIsContactEditing', false);
      }
    },
    async deleteContact(context: ActionContext<ModuleState, State>, payload: DeleteContactPayload): Promise<void> {
      try {
        context.commit('setIsContactDeleting', true);
        await contactService.deleteContact(payload);
        context.commit('setIsContactDeleting', false);
        context.dispatch('fetchContactList');
        context.dispatch('fetchTenantContactList');
      } catch (error) {
        context.commit('setIsContactDeleting', false);
      }
    },
    async sendVerificationEmail(
      context: ActionContext<ModuleState, State>,
      payload: SendVerificationEmailPayload
    ): Promise<void> {
      try {
        context.commit('setIsVerificationEmailSending', true);
        await contactService.sendVerificationEmail(payload);
        context.commit('setIsVerificationEmailSending', false);
      } catch (error) {
        context.commit('setIsVerificationEmailSending', false);
      }
    },
    async fetchConsumerProfile(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsConsumerProfileFetching', true);
        const result = await Promise.all([
          consumerProfileService.getConsumerProfile(),
          context.dispatch('fetchCustomerType')
        ]);
        const consumerProfile = result[0];
        context.commit('setConsumerProfile', consumerProfile);
        context.commit('setIsConsumerProfileFetching', false);
      } catch (error) {
        context.commit('setIsConsumerProfileFetching', false);
      }
    },
    async editConsumerProfile(
      context: ActionContext<ModuleState, State>,
      payload: ConsumerProfileInformation
    ): Promise<void> {
      try {
        context.commit('setIsConsumerProfileEditing', true);
        await consumerProfileService.editConsumerProfile(payload);
        await context.dispatch('fetchConsumerProfile');
        context.commit('setIsConsumerProfileEditing', false);
      } catch (error) {
        context.commit('setIsConsumerProfileEditing', false);
      }
    },
    async fetchEmailNotificationsSettings(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsEmailNotificationsSettingsFetching', true);
        const emailNotificationsSettings:
          EmailNotificationsSettings = await consumerProfileService.getEmailNotificationsSettings();
        context.commit('setEmailNotificationsSettings', emailNotificationsSettings);
        context.commit('setIsEmailNotificationsSettingsFetching', false);
      } catch (error) {
        context.commit('setIsEmailNotificationsSettingsFetching', false);
      }
    },
    async editEmailNotificationsSettings(
      context: ActionContext<ModuleState, State>,
      payload: EmailNotificationsSettings
    ): Promise<void> {
      try {
        context.commit('setIsEmailNotificationsSettingsEditing', true);
        await consumerProfileService.editEmailNotificationsSettings(payload);
        await context.dispatch('fetchEmailNotificationsSettings');
        context.commit('setIsEmailNotificationsSettingsEditing', false);
      } catch (error) {
        context.commit('setIsEmailNotificationsSettingsEditing', false);
      }
    },
    async resetPassword(
      context: ActionContext<ModuleState, State>,
      payload: ResetPasswordPayload
    ): Promise<void> {
      await consumerProfileService.resetPassword(payload);
    },
    async fetchTenantLicenses(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsTenantLicensesFetching', true);
        const licenseKeys: TenantLicensesResponse = await deleteAccountService.getTenantLicenses();
        context.commit('setTenantLicenses', licenseKeys.subscriptions);
        context.commit('setIsTenantLicensesFetching', false);
      } catch (error) {
        context.commit('setIsTenantLicensesFetching', false);
      }
    },
    async fetchTenantStorageUsage(context: ActionContext<ModuleState, State>): Promise<void> {
      try {
        context.commit('setIsTenantStorageUsageFetching', true);
        const usage: number = await deleteAccountService.getTenantStorageUsage();
        context.commit('setTenantStorageUsage', usage);
        context.commit('setIsTenantStorageUsageFetching', false);
      } catch (error) {
        context.commit('setIsTenantStorageUsageFetching', false);
      }
    },
    async requestAccountDeletion(
      context: ActionContext<ModuleState, State>,
      payload: DeleteAccountReason
    ): Promise<void> {
      try {
        await deleteAccountService.requestAccountDeletion(payload);
      } catch (error) {
      }
    },
    async confirmAccountDeletion(
      context: ActionContext<ModuleState, State>,
      payload: DeleteAccountConfirmationPayload
    ): Promise<void> {
      try {
        context.commit('setIsDeleteAccountConfirmationStatusFetching', true);
        const status = await deleteAccountService.confirmAccountDeletion(payload);
        context.commit('setDeleteAccountConfirmationStatus', status);
        context.commit('setIsDeleteAccountConfirmationStatusFetching', false);
      } catch (error) {
        context.commit('setDeleteAccountConfirmationStatus', 'error');
        context.commit('setIsDeleteAccountConfirmationStatusFetching', false);
      }
    },
    async fetchCustomerType(
      context: ActionContext<ModuleState, State>
    ): Promise<void> {
      try {
        const customerType = await deleteAccountService.getCustomerType();
        context.commit('setCustomerType', customerType);
      } catch (error) {
      }
    }
  };
}
